import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@patient-watch/ui/carousel";
import type { BlockT } from "@patient-watch/types";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { cn } from "@/lib/utils";
import { type CarouselImage, parseCarouselImages } from "../utils/parseCarouselImages";

function carouselCopy(locale: string) {
    if (locale === "de-DE") {
        return {
            position: (current: number, total: number) => `Bild ${current} von ${total}`,
            hint: (total: number) => `Wischen oder tippen Sie auf die Pfeile, um alle ${total} Bilder zu sehen.`,
            previous: "Vorheriges Bild",
            next: "Nächstes Bild",
            show: (index: number) => `Bild ${index} anzeigen`,
            named: (letter: string) => `Bild ${letter}`,
        };
    }
    if (locale === "da-DK") {
        return {
            position: (current: number, total: number) => `Billede ${current} af ${total}`,
            hint: (total: number) => `Stryg eller tryk på pilene for at se alle ${total} billeder.`,
            previous: "Forrige billede",
            next: "Næste billede",
            show: (index: number) => `Vis billede ${index}`,
            named: (letter: string) => `Billede ${letter}`,
        };
    }
    return {
        position: (current: number, total: number) => `Picture ${current} of ${total}`,
        hint: (total: number) => `Swipe or use the arrows to see all ${total} pictures.`,
        previous: "Previous picture",
        next: "Next picture",
        show: (index: number) => `Show picture ${index}`,
        named: (letter: string) => `Picture ${letter}`,
    };
}

/** Which picture of a carousel is showing. Lets a parent react to, and move, the carousel. */
export function useCarouselSelection() {
    const [api, setApi] = useState<CarouselApi>();
    const [selected, setSelected] = useState(0);

    useEffect(() => {
        if (!api) return;
        const onSelect = () => setSelected(api.selectedScrollSnap());
        onSelect();
        api.on("select", onSelect);
        api.on("reInit", onSelect);
        return () => {
            api.off("select", onSelect);
            api.off("reInit", onSelect);
        };
    }, [api]);

    return { api, setApi, selected };
}

interface ImageCarouselProps extends ReturnType<typeof useCarouselSelection> {
    images: CarouselImage[];
    label?: string | null;
    /**
     * A shorter picture with no caption or swipe hint, for a page that already says what each picture is,
     * such as a count row under it.
     */
    compact?: boolean;
    /** Names each picture by its letter (Picture A, Picture B ...) above the position count. */
    lettered?: boolean;
}

/** Several images shown one at a time, with arrows, dots and a "Picture 2 of 5" count, so it is clear there are more. */
export function ImageCarousel({
    images,
    label,
    compact = false,
    lettered = false,
    api,
    setApi,
    selected,
}: ImageCarouselProps) {
    const { locale } = useAppLocale();
    const copy = carouselCopy(locale);

    if (images.length === 0) return null;
    const total = images.length;

    return (
        <div data-anno="block-image-carousel" className="space-y-3">
            <Carousel setApi={setApi} aria-label={label ?? undefined}>
                <CarouselContent>
                    {images.map((image, index) => (
                        <CarouselItem key={image.src} aria-label={copy.position(index + 1, total)}>
                            <img
                                src={image.src}
                                alt={image.alt}
                                className={cn("mx-auto w-auto object-contain", compact ? "max-h-64" : "max-h-72")}
                            />
                            {image.alt && !compact ? <p className="mt-2 text-center text-sm">{image.alt}</p> : null}
                        </CarouselItem>
                    ))}
                </CarouselContent>
            </Carousel>

            {total > 1 ? (
                <div className="items-center gap-3 grid grid-cols-[1fr_auto_1fr]">
                    <button
                        type="button"
                        onClick={() => api?.scrollPrev()}
                        disabled={selected === 0}
                        className="flex justify-self-start items-center gap-1 disabled:opacity-40 py-1 pr-3 pl-2 border dark:border-slate-600 rounded-full h-11 font-medium text-sm"
                    >
                        <ChevronLeft size={20} aria-hidden />
                        {copy.previous}
                    </button>
                    <div className="flex flex-col items-center gap-2">
                        {lettered ? (
                            <span className="font-semibold text-base">
                                {copy.named(String.fromCharCode(65 + selected))}
                            </span>
                        ) : null}
                        <output aria-live="polite" className="font-medium text-sm">
                            {copy.position(selected + 1, total)}
                        </output>
                        <div className="flex gap-2">
                            {images.map((image, index) => (
                                <button
                                    key={image.src}
                                    type="button"
                                    onClick={() => api?.scrollTo(index)}
                                    aria-label={copy.show(index + 1)}
                                    aria-current={index === selected}
                                    className={cn(
                                        "rounded-full w-4 h-4 bg-slate-300",
                                        index === selected && "bg-slate-800"
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => api?.scrollNext()}
                        disabled={selected === total - 1}
                        className="flex justify-self-end items-center gap-1 disabled:opacity-40 py-1 pr-2 pl-3 border dark:border-slate-600 rounded-full h-11 font-medium text-sm"
                    >
                        {copy.next}
                        <ChevronRight size={20} aria-hidden />
                    </button>
                </div>
            ) : null}
            {total > 1 && !compact ? (
                <p className="text-center text-muted-foreground text-xs">{copy.hint(total)}</p>
            ) : null}
        </div>
    );
}

export default function ImageCarouselBlock({ block }: { block: BlockT }) {
    const selection = useCarouselSelection();
    return <ImageCarousel images={parseCarouselImages(block.content)} label={block.caption} {...selection} />;
}
